import PublicIcon from "@mui/icons-material/Public";
import { TextField } from '@mui/material';
import { FC, Fragment } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import Dropzone from 'react-dropzone';
import { useFormContext } from 'react-hook-form';
import { systemFields } from '../../Utils/common';
import CountryInput from '../Country';
import './style.scss';

import BorderLessInput, {
  PlaceholderBoxSpacing,
  PlaceholderChildContainer,
  PlaceholderLabelSpacing,
  PlaceholderParentContainer,
} from "../Custom/BorderLessInput";

interface Props {
  value: any;
  selectedData: any;
  selectedType: string;
  optionIndex: any;
  setOptionIndex: (e: any) => void;
  storeHistory: (field: string, value: any, type: string, optional?: any) => void;
  setRedoState: (redoState: any) => void;
  isMultiInput?: boolean;
}

const Input: FC<Props> = ({ value, selectedType, optionIndex, setOptionIndex, storeHistory, setRedoState, isMultiInput }) => {
  const { getValues, watch, setValue } = useFormContext();

  const handleDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    const updatedFields = [...watch('options')];
    const [movedItem] = updatedFields.splice(sourceIndex, 1);
    updatedFields.splice(destinationIndex, 0, movedItem);
    storeHistory('options', getValues('options'), "slides")
    setValue('options', updatedFields);
    setOptionIndex(destinationIndex);
    setRedoState([])
  };

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="fields">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef} className="input-container">
              {watch('options')?.map((field: any, index: number) => (
                <Draggable key={field?.id} draggableId={String(index)} index={index} isDragDisabled={systemFields.includes(selectedType)}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className="input"
                      onClick={() => {
                        setOptionIndex(index);
                      }}
                    >
                      <div>
                        {isMultiInput && <div><span className='qno'>{`${index + 1}. `}</span>{getValues(`options[${index}].question`)}</div>}
                        {['text', 'email', 'url', 'number', 'decimal'].includes(getValues(`options[${index}].type`)) ? (
                          <Fragment>
                            <PlaceholderLabelSpacing>
                              <BorderLessInput
                                color="white"
                                placeHolder='Label (Optional)'
                                value={watch(`options[${index}].label`) || ''}
                                setValue={(value) => {
                                  setValue(`options[${index}].label`, value);
                                }}
                              />
                            </PlaceholderLabelSpacing>
                            <PlaceholderParentContainer>
                              <TextField
                                id="name"
                                InputLabelProps={{ shrink: true }}
                                name="name"
                                variant="standard"
                                disabled={true}
                                key={field?.id}
                                sx={{ width: "100%" }}
                              />
                              <PlaceholderChildContainer>
                                <BorderLessInput
                                  color=""
                                  fullWidth
                                  placeHolder='Placeholder (Optional)'
                                  value={watch(`options[${index}].placeholder`) || ''}
                                  setValue={(value) => {
                                    setValue(`options[${index}].placeholder`, value);
                                  }}
                                />
                              </PlaceholderChildContainer>
                            </PlaceholderParentContainer>
                          </Fragment>
                        ) : getValues(`options[${index}].type`) === 'file' ? (
                          <Dropzone disabled={true}>
                            {({ getRootProps }) => (
                              <div className="file-uploader" {...getRootProps()}>
                                <label htmlFor="actual-btn">Choose or drop the File</label>
                                <span id="file-chosen">{'No file chosen'}</span>
                              </div>
                            )}
                          </Dropzone>
                        ) : getValues(`options[${index}].type`) === 'textArea' ? (
                          <Fragment>
                            <BorderLessInput
                              color="white"
                              placeHolder='Label (Optional)'
                              value={watch(`options[${index}].label`) || ''}
                              setValue={(value) => {
                                setValue(`options[${index}].label`, value);
                              }}
                            />
                            <PlaceholderParentContainer>
                              <TextField
                                key={field?.id}
                                className="text-"
                                InputLabelProps={{ shrink: false }}
                                minRows={4}
                                multiline
                                sx={{ width: "100%" }}
                                value=""
                                variant="outlined"
                              />
                              <PlaceholderChildContainer>
                                <PlaceholderBoxSpacing>
                                  <BorderLessInput
                                    color=""
                                    fullWidth
                                    placeHolder='Placeholder (Optional)'
                                    value={watch(`options[${index}].placeholder`) || ''}
                                    setValue={(value) => {
                                      setValue(`options[${index}].placeholder`, value);
                                    }}
                                  />
                                </PlaceholderBoxSpacing>
                              </PlaceholderChildContainer>
                            </PlaceholderParentContainer>
                          </Fragment>
                        ) : getValues(`options[${index}].type`) === 'scanner' ? (
                          <Fragment>
                            <PlaceholderLabelSpacing>
                              <BorderLessInput
                                color="white"
                                placeHolder='Label (Optional)'
                                value={watch(`options[${index}].label`) || ''}
                                setValue={(value) => {
                                  setValue(`options[${index}].label`, value);
                                }}
                              />
                            </PlaceholderLabelSpacing>
                            <PlaceholderParentContainer>
                              <TextField
                                id="name"
                                InputLabelProps={{ shrink: true }}
                                name="name"
                                variant="standard"
                                disabled={true}
                                key={field?.id}
                                sx={{ width: "100%" }}
                                InputProps={{
                                  readOnly: true,
                                  endAdornment: (
                                    <svg className='scanner-svg' xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 96 960 960" width="24" fill="currentColor">
                                      <path d="M120 896v-180h60v120h120v60H120Zm540 0v-60h120V716h60v180H660ZM120 416V236h180v60H180v120h-60Zm660 0V296H660v-60h180v180h-60ZM260 736q-24 0-42-18t-18-42V476q0-24 18-42t42-18h440q24 0 42 18t18 42v200q0 24-18 42t-42 18H260Zm0-60h440V476H260v200Zm0 0V476v200Z" />
                                    </svg>
                                  )
                                }}
                              />
                              <PlaceholderChildContainer>
                                <BorderLessInput
                                  color=""
                                  fullWidth
                                  placeHolder='Placeholder (Optional)'
                                  value={watch(`options[${index}].placeholder`) || ''}
                                  setValue={(value) => {
                                    setValue(`options[${index}].placeholder`, value);
                                  }}
                                />
                              </PlaceholderChildContainer>
                            </PlaceholderParentContainer>
                          </Fragment>
                        ) : getValues(`options[${index}].type`) === 'phoneNumber' ? (
                          <Fragment>
                            <PlaceholderLabelSpacing>
                              <BorderLessInput
                                color="white"
                                placeHolder='Label (Optional)'
                                value={watch(`options[${index}].label`) || ''}
                                setValue={(value) => {
                                  setValue(`options[${index}].label`, value);
                                }}
                              />
                            </PlaceholderLabelSpacing>
                            <div style={{ alignItems: 'center', display: 'flex', height: '50px', width: '100%' }}>
                              <div style={{ alignItems: 'center', display: 'flex', border: '1px solid white', borderRight: 'none', borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px', height: '50px', justifyContent: 'center', width: '65px' }}>
                                <PublicIcon sx={{ color: "white" }} />
                              </div>
                              <div style={{ alignItems: 'center', border: '1px solid white', borderTopRightRadius: '10px', borderBottomRightRadius: '10px', display: 'flex', height: '50px', width: 'calc(100% - 65px)' }}>
                                <div style={{ marginLeft: '10px', width: '100%' }}>
                                  <BorderLessInput
                                    color="white"
                                    fullWidth
                                    placeHolder='Placeholder (Optional)'
                                    value={watch(`options[${index}].placeholder`) || ''}
                                    setValue={(value) => {
                                      setValue(`options[${index}].placeholder`, value);
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </Fragment>
                        ) : getValues(`options[${index}].type`) === 'country' ? (
                          <Fragment>
                            <PlaceholderLabelSpacing>
                              <BorderLessInput
                                color="white"
                                placeHolder='Label (Optional)'
                                value={watch(`options[${index}].label`) || ''}
                                setValue={(value) => {
                                  setValue(`options[${index}].label`, value);
                                }}
                              />
                            </PlaceholderLabelSpacing>
                            <CountryInput fromInput={true} />
                          </Fragment>
                        ) :
                          <></>}
                      </div>
                      {!systemFields.includes(selectedType) && <svg className="svg-icon"
                        viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M298.666667 810.666667v-85.333334h85.333333v85.333334H298.666667m170.666666 0v-85.333334h85.333334v85.333334h-85.333334m170.666667 0v-85.333334h85.333333v85.333334h-85.333333m-341.333333-170.666667v-85.333333h85.333333v85.333333H298.666667m170.666666 0v-85.333333h85.333334v85.333333h-85.333334m170.666667 0v-85.333333h85.333333v85.333333h-85.333333m-341.333333-170.666667V384h85.333333v85.333333H298.666667m170.666666 0V384h85.333334v85.333333h-85.333334m170.666667 0V384h85.333333v85.333333h-85.333333M298.666667 298.666667V213.333333h85.333333v85.333334H298.666667m170.666666 0V213.333333h85.333334v85.333334h-85.333334m170.666667 0V213.333333h85.333333v85.333334h-85.333333z"
                          fill="" />
                      </svg>}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

    </>
  );
};

export default Input;
